<template>
  <div class="pb-6" ref="container" :style="maxHeight ? `height: ${maxHeight}px;` : ''">
    <transition name="fade" mode="out-in">
      <div class="flex items-center justify-center py-32" v-if="loading">
        <i v-html="$icons.loading"></i>
      </div>
      <div class="flex h-full flex-col items-center gap-6" v-else>
        <!-- STRATEGY NAME -->
        <div class="flex flex-col items-center">
          <h1 class="text-ontick-steel-900 text-2xl font-semibold dark:text-gray-200">
            {{ data.investment.strategy_name }}
          </h1>
          <h2 class="text-ontick-steel-700 text-sm dark:text-gray-300">
            Ativo operado: {{ data.investment.strategy_symbol }}
          </h2>
        </div>

        <!-- ACCOUNT INFO -->
        <div class="flex items-center justify-center gap-2">
          <ontick-broker-logo-component :broker="getSelectedAccount.broker_name" />
          <span class="text-ontick-steel-900 text-lg font-semibold dark:text-gray-200">
            {{ getSelectedAccount.broker_name }}
          </span>
          <span class="text-ontick-steel-400 text-lg font-medium dark:text-gray-400">
            (#{{ getSelectedAccount.account_number }})
          </span>
          <ontick-account-type-badge-component :accountType="getSelectedAccount.account_type" />
        </div>
        <transition-group name="fade" mode="out-in" class="flex h-full w-full items-center justify-center">
          <div key="1" v-if="!showSuccessMessage" class="flex w-full flex-col items-center gap-6 transition-all">
            <!-- PARAMS LIST -->
            <select
              ref="param_select"
              v-model="selectedParam"
              name="param"
              id="param"
              class="border-ontick-steel-300 bg-ontick-steel-100 dark:border-ontick-carbon-500 dark:bg-ontick-graphite-500 w-full rounded border px-3 py-2 text-sm shadow-sm"
            >
              <option v-for="param in paramsList" :key="param.param_id" :value="param.param_id">
                {{ param.param_name }} (#{{ param.param_id }})
              </option>
            </select>

            <!-- OPERATION INFO -->
            <div
              class="border-ontick-steel-300 bg-ontick-steel-100 dark:border-ontick-carbon-500 dark:bg-ontick-graphite-500 flex w-full flex-col gap-2 rounded border p-3 text-sm shadow-sm"
            >
              <dl class="grid grid-cols-2" v-for="item in operationInfo" :key="item.label">
                <dt>
                  {{ item.label }}
                </dt>
                <dd :class="item.class || ''">
                  {{ item.value }}
                </dd>
              </dl>
            </div>

            <!-- SECURITY CODE -->
            <div class="flex flex-col items-center gap-6" v-if="getSelectedAccount.account_type == 'PRD'">
              <div>
                <h3 class="text-center text-2xl">Assinatura eletrônica</h3>
                <p class="text-center text-xs">
                  Digite sua assinatura eletrônica de 4 digitos para confirmar esta ação.
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <ontick-pin-code-input-component v-model="pinCode" />
                <a
                  href="https://ajuda.ontick.com.br/portal/pt-br/kb/articles/como-gerar-nova-assinatura-eletronica-ontick"
                  target="_blank"
                  class="hover:text-ontick-blue-500 text-xs font-medium"
                  >Esqueceu sua assinatura eletrônica? Clique aqui.</a
                >
              </div>
            </div>

            <!-- START BUTTON -->
            <button
              :disabled="buttonDisabled"
              @click="startInvestment"
              class="disabled:bg-ontick-steel-200 dark:disabled:bg-ontick-steel-500 bg-ontick-blue-900 inline-flex w-4/6 items-center justify-center rounded-md px-6 py-3 text-sm font-bold uppercase text-white shadow-lg filter transition-all duration-150 ease-in-out hover:brightness-110 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500"
            >
              <i v-html="$icons.loading" v-if="loadingStart"></i>
              <span v-else> Iniciar estratégia </span>
            </button>
          </div>
          <div v-else-if="showSuccessMessage" key="2" class="w-full transition-all">
            <ontick-success-component
              key="2"
              :title="
                data.investment.type === 'PORTFOLIO'
                  ? 'Portfólio iniciado com sucesso'
                  : 'Estratégia iniciada com sucesso'
              "
              :active="showSuccessMessage"
            />
          </div>
        </transition-group>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OntickAccountTypeBadgeComponent from "../../../../../shared/components/basic/ontick-account-type-badge.component.vue";
import ontickBrokerLogoComponent from "../../../../../shared/components/basic/ontick-broker-logo.component.vue";
import OntickPinCodeInputComponent from "../../../../../shared/components/basic/ontick-pin-code-input.component.vue";
import OntickSuccessComponent from "../../../../../shared/components/basic/ontick-success.component.vue";

export default {
  components: {
    ontickBrokerLogoComponent,
    OntickAccountTypeBadgeComponent,
    OntickPinCodeInputComponent,
    OntickSuccessComponent,
  },
  props: {
    data: {
      type: Object,
    },
  },

  data() {
    return {
      loading: true,
      loadingStart: false,
      paramsList: [],
      selectedParam: null,
      pinCode: null,
      showSuccessMessage: false,
      maxHeight: null,
    };
  },

  computed: {
    ...mapGetters({
      getSelectedAccount: "accountTypes/getSelectedAccount",
    }),
    isPortfolio() {
      return this.data.investment.type === "PORTFOLIO";
    },
    buttonDisabled() {
      if (this.selectedParam === null) return true;

      if (this.getSelectedAccount.account_type == "PRD") {
        if (this.pinCode === null) return true;

        return this.pinCode.length !== 4;
      }

      return false;
    },
    operationInfo() {
      // if (!this.isPortfolio) {
      const param = this.paramsList.find((item) => item.param_id === this.selectedParam);
      const param_data = param?.param_data || param;

      let info = [];
      if (param_data.volume_type === "QUANTITY") {
        info.push({
          label: "Contratos",
          value: this.data.selectedVolume,
        });
      } else if (param_data.volume_type === "FIN_VOLUME") {
        info.push({
          label: "Volume por operação",
          value: this.$filters.currency(this.data.selectedVolume),
        });
      }
      if (param_data.fin_sl) {
        info.push({
          label: "Perda máxima",
          value: this.$filters.currency(param_data.fin_sl),
          class: "text-ontick-red-600",
        });
      }
      if (param_data.fin_tp) {
        info.push({
          label: "Ganho máximo",
          value: this.$filters.currency(param_data.fin_tp),
          class: "text-ontick-green-600",
        });
      }
      if (this.data.investment.min_capital) {
        let value;
        if (param_data.volume_type === "FIN_VOLUME") {
          value = this.data.selectedVolume * param_data.max_positions;
        } else {
          value = this.data.selectedVolume * (this.data.investment.min_capital / this.data.investment.volume_step);
        }
        info.push({
          label: "Capital recomendado",
          value: this.$filters.currency(value),
        });
      }
      return info;
      // }
      // }
      // return;
    },
  },

  methods: {
    ...mapActions({
      getInvestments: "investment/getInvestments",
    }),
    async startInvestment() {
      this.loadingStart = true;
      this.maxHeight = this.$refs.container.offsetHeight;
      const selectedParam = this.paramsList.find((item) => item.param_id === this.selectedParam);
      if (this.isPortfolio) {
        try {
          const body = {
            account_id: this.getSelectedAccount.account_id,
            investment_id: this.data.investment.investment_id,
            param_id: selectedParam.param_id,
            user_code: this.pinCode || undefined,
            typed_multiple: this.data.selectedVolume,
          };
          const response = await this.$api.post(this.$endpoint.START_INVESTMENT_PORTFOLIO, body);
          // console.log(response.data);
          this.showSuccessMessage = true;
          try {
            const response = await this.getInvestments();
            // console.log(response);
            setTimeout(() => {
              this.$emit("close");
            }, 3000);
          } catch (err) {
            throw err;
          }
        } catch (err) {
          console.error(err);
          this.$swal("Erro", err.data.msg, "error");
          this.$emit("close");
        } finally {
          this.loadingStart = false;
        }
      } else {
        try {
          const body = {
            account_id: this.getSelectedAccount.account_id,
            investment_id: this.data.investment.investment_id,
            param_id: selectedParam.param_id,
            typed_volume: this.data.selectedVolume,
            volume_type: selectedParam.param_data.volume_type,
            user_code: this.pinCode || undefined,
          };
          const response = await this.$api.post(this.$endpoint.START_INVESTMENT, body);
          // console.log(response.data);
          this.showSuccessMessage = true;
          try {
            const response = await this.getInvestments();
            // console.log(response);
            setTimeout(() => {
              this.$emit("close");
            }, 3000);
          } catch (err) {
            console.error(err);
            this.$swal("Erro", err.data.msg, "error");
            this.$emit("close");
          }
        } catch (err) {
          console.error(err);
          this.$swal("Erro", err.data.msg, "error");
          this.$emit("close");
        } finally {
          this.loadingStart = false;
        }
      }
    },
    async getParamsList(investment_id) {
      this.loading = true;
      try {
        const response = await this.$api.get(`${this.$endpoint.LIST_PARAMS}/${investment_id}`);
        this.paramsList = response.data;
        this.selectedParam = this.paramsList.find((item) => item.selected).param_id;
        // console.log(response.data);
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    enter(el, done) {
      TweenMax.fromTo(
        el,
        0.5,
        {
          autoAlpha: 0,
          scale: 1.5,
        },
        {
          autoAlpha: 1,
          scale: 1,
          transformOrigin: "50% 50%",
          ease: Power4.easeOut,
          onComplete: done,
        }
      );
    },
    leave(el, done) {
      TweenMax.fromTo(
        el,
        0.5,
        {
          autoAlpha: 1,
          scale: 1,
        },
        {
          autoAlpha: 0,
          scale: 0.8,
          ease: Power4.easeOut,
          onComplete: done,
        }
      );
    },
  },

  mounted() {
    this.getParamsList(this.data.investment.investment_id);
  },
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to
/* .component-fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
